import React, { useId, useRef, useState } from 'react'

type Props = {
  /** The text shown on hover/focus. Also the trigger's accessible description. */
  content: string
  /** The trigger. Rendered inside a focusable wrapper so keyboard users can reach the tip. */
  children: React.ReactNode
}

/**
 * A minimal accessible tooltip. Helix has none and the SPA does not depend on Flowbite, so
 * this covers the WAI-ARIA tooltip pattern with Tailwind only:
 *
 * - shows on hover AND keyboard focus (never mouse-only), and Escape hides it;
 * - a tap toggles it, since touch screens have no hover. A tap also emulates `mouseenter` and
 *   `focus` (which open it) before its `click`, so the click restores the opposite of the state
 *   from BEFORE the tap, not of whatever the emulated events just set. A mouse click does not
 *   toggle: hover already opened it, and a click must not hide what the pointer is resting on;
 * - the text stays in the DOM and is linked by `aria-describedby`, so a screen reader reads it
 *   with the trigger whether or not it is visible.
 */
const Tooltip: React.FC<Props> = ({ content, children }) => {
  const id = useId()
  const [open, setOpen] = useState(false)
  // The open state when a touch/pen press began; null when the last press was not a tap.
  const openBeforeTap = useRef<boolean | null>(null)

  return (
    <span className="relative inline-flex">
      <span
        tabIndex={0}
        aria-describedby={id}
        onPointerDown={(event) => {
          openBeforeTap.current = event.pointerType === 'mouse' ? null : open
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => {
          if (openBeforeTap.current === null) return
          setOpen(!openBeforeTap.current)
          openBeforeTap.current = null
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false)
        }}
        // `after:-inset-2` widens the tap target past the small badge without moving the layout.
        className="focus-visible:ring-brand-600/20 relative inline-flex cursor-help rounded-full after:absolute after:-inset-2 after:content-[''] focus:outline-none focus-visible:ring-2"
      >
        {children}
      </span>
      <span
        id={id}
        role="tooltip"
        className={
          open
            ? // Helix's dark-surface token, not `bg-gray-900`: Helix defines only gray-100, so
              // every other gray step resolves to an unset variable and renders transparent.
              // bg-dark-strong is a dark surface in every Helix theme (incl. dark mode), and
              // text-white is white in all of them, so the pair stays readable without `dark:`.
              'text-text-white absolute right-0 bottom-full z-10 mb-2 w-64 rounded-xl bg-[var(--color-bg-dark-strong)] px-3 py-2 text-xs leading-5 shadow-xl'
            : 'sr-only'
        }
      >
        {content}
      </span>
    </span>
  )
}

export default Tooltip
